import { useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  Switch,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PsychologyIcon from "@mui/icons-material/Psychology";
import GroupsIcon from "@mui/icons-material/Groups";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import BatterySaverIcon from "@mui/icons-material/BatterySaver";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import { AuthContext, useAuth } from "../context/AuthContext";
import { useThemeMode } from "../context/ThemeContext";
import { useSnack } from "../hooks/useSnack";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import HandScanIcon from "../components/icons/HandScanIcon";
import { resolveLabel } from "../utils/labelUtils";

const MANAS = [
  { key: "us_logique",     labelKey: "gauges.logique" },
  { key: "us_social",     labelKey: "gauges.social" },
  { key: "us_sensoriel",   labelKey: "gauges.sensoriel" },
  { key: "us_passion",     labelKey: "gauges.passion" },
  { key: "us_motivation", labelKey: "gauges.motivation" },
  { key: "us_reserve",   labelKey: "gauges.reserve" },
  { key: "us_stamina",     labelKey: "gauges.stamina" },
];

const CATEGORY_ORDER_S = ["trajets", "travail", "détente", "maison", "famille-amis", "sensoriel", "autres"];
// mapping catégorie (valeur DB) → clé i18n
const CAT_I18N_KEY = (c) => c === "détente" ? "detente" : c;
const SIT_MANA_KEYS = ["us_logique","us_social","us_sensoriel","us_passion","us_motivation","us_reserve","us_stamina"];
const categorizeSitS = (row) => {
  const hasPos = SIT_MANA_KEYS.some((k) => row[k] > 0);
  const hasNeg = SIT_MANA_KEYS.some((k) => row[k] < 0);
  if (hasPos && !hasNeg) return "positive";
  if (hasNeg && !hasPos) return "negative";
  return "mixed";
};
const POLARITY_COLOR_S = { positive: "#86efac", negative: "#fca5a5", mixed: "#93c5fd" };

// ─── Tableau des situations ─────────────────────────────────────────────────
function SituationsTable({ appTheme }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage === "en" ? "en" : "fr";
  const [situations, setSituations] = useState([]);
  const [editing, setEditing] = useState({}); // { [us_ID]: { ...fields } }
  const [newRow, setNewRow] = useState(null); // champs de la nouvelle situation
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPolarity, setFilterPolarity] = useState("");
  const [filterOrigin, setFilterOrigin] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const { showSnack, Snack } = useSnack();

  useEffect(() => {
    fetch("/api/situations", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setSituations)
      .catch(() => {});
  }, []);

  const startEdit = (sit) => {
    setEditing((prev) => ({
      ...prev,
      [sit.us_ID]: {
        us_label_fr: sit.us_label_fr || "",
        us_label_en: sit.us_label_en || "",
        us_active: sit.us_active,
        us_logique: sit.us_logique,
        us_social: sit.us_social,
        us_sensoriel: sit.us_sensoriel,
        us_passion: sit.us_passion,
        us_motivation: sit.us_motivation,
        us_reserve: sit.us_reserve,
        us_stamina: sit.us_stamina,
        us_category: sit.us_category || "autres",
        us_subcategory: sit.us_subcategory || "",
      },
    }));
  };

  const cancelEdit = (id) => {
    setEditing((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
  };

  const saveEdit = async (id) => {
    const body = editing[id];
    try {
      const res = await fetch(`/api/situations/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setSituations((prev) => prev.map((s) => (s.us_ID === id ? updated : s)));
      cancelEdit(id);
      showSnack(t("settings.situations.saved"), "success");
    } catch {
      showSnack(t("common.errorUpdate"), "error");
    }
  };

  const toggleActive = async (sit) => {
    try {
      const res = await fetch(`/api/situations/${sit.us_ID}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ us_active: !sit.us_active }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setSituations((prev) =>
        prev.map((s) => (s.us_ID === sit.us_ID ? updated : s)),
      );
    } catch {
      showSnack(t("common.errorToggle"), "error");
    }
  };

  const deleteSit = async (sit) => {
    if (sit.us_sit_ID !== null) {
      showSnack(t("settings.situations.deactivateInstead"), "warning");
      return;
    }
    try {
      const res = await fetch(`/api/situations/${sit.us_ID}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      setSituations((prev) => prev.filter((s) => s.us_ID !== sit.us_ID));
      showSnack(t("settings.situations.deleted"), "success");
    } catch {
      showSnack(t("common.errorDelete"), "error");
    }
  };

  const addNewRow = () => {
    const empty = { us_label_fr: "", us_label_en: "", us_category: "autres", us_subcategory: "" };
    for (const m of MANAS) empty[m.key] = 0;
    setNewRow(empty);
  };

  const saveNewRow = async () => {
    if (!newRow.us_label_fr?.trim() && !newRow.us_label_en?.trim()) {
      showSnack(t("common.labelRequired"), "warning");
      return;
    }
    try {
      const res = await fetch("/api/situations", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newRow),
      });
      if (!res.ok) throw new Error();
      const created = await res.json();
      setSituations((prev) => [...prev, created]);
      setNewRow(null);
      showSnack(t("settings.situations.added"), "success");
    } catch {
      showSnack(t("common.errorAdd"), "error");
    }
  };

  const cellSx = {
    color: appTheme.text,
    borderColor: appTheme.text + "33",
    padding: "6px 8px",
    fontSize: "0.75rem",
  };

  const inputSx = {
    width: 52,
    "& input": {
      color: appTheme.text,
      textAlign: "center",
      padding: "4px 6px",
      fontSize: "0.75rem",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "55" },
  };

  const filteredSituations = situations.filter((sit) => {
    if (searchQuery.trim() !== "") {
      const q = searchQuery.trim().toLowerCase();
      const matchedManas = MANAS.filter((m) => t(m.labelKey).toLowerCase().includes(q));
      const matchesSearch = resolveLabel(sit, "us_label", lang).toLowerCase().includes(q)
        || (matchedManas.length > 0 && matchedManas.some((m) => sit[m.key] !== 0));
      if (!matchesSearch) return false;
    }
    if (filterPolarity && categorizeSitS(sit) !== filterPolarity) return false;
    if (filterOrigin === "default" && sit.us_sit_ID === null) return false;
    if (filterOrigin === "custom" && sit.us_sit_ID !== null) return false;
    if (filterStatus === "active" && !sit.us_active) return false;
    if (filterStatus === "inactive" && sit.us_active) return false;
    return true;
  });

  const toggleSx = {
    color: appTheme.text + "99",
    borderColor: appTheme.text + "33",
    fontSize: "0.65rem",
    py: 0.3,
    px: 0.8,
    "&.Mui-selected": { color: appTheme.text, backgroundColor: appTheme.text + "18" },
  };

  return (
    <Box data-tour="tour-settings-situations" sx={{ overflowX: "auto" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5, alignItems: "center" }}>
        <TextField
          size="small"
          placeholder={t("common.search")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: 300,
            "& input": { color: appTheme.text, fontSize: "0.8rem", padding: "6px 10px" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "44" },
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filterPolarity}
          onChange={(_, v) => setFilterPolarity(v ?? "")}
        >
          <ToggleButton value="" sx={toggleSx}>{t("common.filter.all")}</ToggleButton>
          <ToggleButton value="positive" sx={toggleSx}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: POLARITY_COLOR_S.positive, mr: 0.5 }} />
            {t("common.filter.positive")}
          </ToggleButton>
          <ToggleButton value="negative" sx={toggleSx}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: POLARITY_COLOR_S.negative, mr: 0.5 }} />
            {t("common.filter.negative")}
          </ToggleButton>
          <ToggleButton value="mixed" sx={toggleSx}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: POLARITY_COLOR_S.mixed, mr: 0.5 }} />
            {t("common.filter.mixed")}
          </ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filterOrigin}
          onChange={(_, v) => setFilterOrigin(v ?? "")}
        >
          <ToggleButton value="" sx={toggleSx}>{t("common.filter.all")}</ToggleButton>
          <ToggleButton value="default" sx={toggleSx}>{t("common.filter.default")}</ToggleButton>
          <ToggleButton value="custom" sx={toggleSx}>{t("common.filter.custom")}</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filterStatus}
          onChange={(_, v) => setFilterStatus(v ?? "")}
        >
          <ToggleButton value="" sx={toggleSx}>{t("common.filter.all")}</ToggleButton>
          <ToggleButton value="active" sx={toggleSx}>{t("common.filter.active")}</ToggleButton>
          <ToggleButton value="inactive" sx={toggleSx}>{t("common.filter.inactive")}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Table size="small" sx={{ minWidth: 960 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...cellSx, fontWeight: "bold" }}>
              {t("settings.situations.headerLabel")}
            </TableCell>
            {MANAS.map((m) => (
              <TableCell
                key={m.key}
                align="center"
                sx={{ ...cellSx, fontWeight: "bold" }}
              >
                {t(m.labelKey)}
              </TableCell>
            ))}
            <TableCell align="center" sx={{ ...cellSx, fontWeight: "bold" }}>{t("common.active")}</TableCell>
            <TableCell align="center" sx={{ ...cellSx, fontWeight: "bold" }}>{t("common.category")}</TableCell>
            <TableCell align="center" sx={{ ...cellSx, fontWeight: "bold" }}>{t("common.subcategory")}</TableCell>
            <TableCell align="center" sx={{ ...cellSx, fontWeight: "bold" }}>{t("common.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredSituations.map((sit) => {
            const isEditing = !!editing[sit.us_ID];
            const row = isEditing ? editing[sit.us_ID] : sit;
            return (
              <TableRow
                key={sit.us_ID}
                sx={{ opacity: sit.us_active ? 1 : 0.45 }}
              >
                {/* Label + polarité */}
                <TableCell sx={cellSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Tooltip title={categorizeSitS(row)}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: POLARITY_COLOR_S[categorizeSitS(row)], flexShrink: 0 }} />
                    </Tooltip>
                  {isEditing ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <TextField
                        size="small"
                        placeholder="FR"
                        value={row.us_label_fr}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [sit.us_ID]: { ...prev[sit.us_ID], us_label_fr: e.target.value },
                          }))
                        }
                        sx={{ "& input": { color: appTheme.text, padding: "4px 8px", fontSize: "0.8rem" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "55" } }}
                      />
                      <TextField
                        size="small"
                        placeholder="EN"
                        value={row.us_label_en}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [sit.us_ID]: { ...prev[sit.us_ID], us_label_en: e.target.value },
                          }))
                        }
                        sx={{ "& input": { color: appTheme.text, padding: "4px 8px", fontSize: "0.8rem" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "55" } }}
                      />
                    </Box>
                  ) : (
                    resolveLabel(sit, "us_label", lang)
                  )}
                  </Box>
                </TableCell>

                {/* Champs mana */}
                {MANAS.map((m) => (
                  <TableCell key={m.key} align="center" sx={cellSx}>
                    {isEditing ? (
                      <TextField
                        size="small"
                        type="number"
                        value={row[m.key]}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [sit.us_ID]: {
                              ...prev[sit.us_ID],
                              [m.key]: parseInt(e.target.value) || 0,
                            },
                          }))
                        }
                        sx={inputSx}
                      />
                    ) : (
                      <span
                        style={{
                          color:
                            sit[m.key] > 0
                              ? "#4ade80"
                              : sit[m.key] < 0
                                ? "#f87171"
                                : appTheme.text,
                        }}
                      >
                        {sit[m.key] > 0 ? `+${sit[m.key]}` : sit[m.key]}
                      </span>
                    )}
                  </TableCell>
                ))}

                {/* Actif */}
                <TableCell align="center" sx={cellSx}>
                  <Switch
                    checked={isEditing ? row.us_active : sit.us_active}
                    size="small"
                    onChange={() =>
                      isEditing
                        ? setEditing((prev) => ({
                            ...prev,
                            [sit.us_ID]: {
                              ...prev[sit.us_ID],
                              us_active: !prev[sit.us_ID].us_active,
                            },
                          }))
                        : toggleActive(sit)
                    }
                    sx={{
                      "& .MuiSwitch-track": {
                        backgroundColor: appTheme.text + "44",
                      },
                    }}
                  />
                </TableCell>

                {/* Catégorie */}
                <TableCell align="center" sx={cellSx}>
                  {isEditing ? (
                    <Select
                      size="small"
                      value={row.us_category || "autres"}
                      onChange={(e) => setEditing((prev) => ({ ...prev, [sit.us_ID]: { ...prev[sit.us_ID], us_category: e.target.value } }))}
                      sx={{ fontSize: "0.7rem", minWidth: 90, color: appTheme.text }}
                    >
                      {CATEGORY_ORDER_S.map((c) => (
                        <MenuItem key={c} value={c} sx={{ fontSize: "0.75rem" }}>{t("categories." + CAT_I18N_KEY(c))}</MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <span>{sit.us_category ? t("categories." + CAT_I18N_KEY(sit.us_category), sit.us_category) : "—"}</span>
                  )}
                </TableCell>

                {/* Sous-catégorie */}
                <TableCell align="center" sx={cellSx}>
                  {isEditing ? (
                    <TextField
                      size="small"
                      placeholder={t("common.optionalPlaceholder")}
                      value={row.us_subcategory || ""}
                      onChange={(e) => setEditing((prev) => ({ ...prev, [sit.us_ID]: { ...prev[sit.us_ID], us_subcategory: e.target.value } }))}
                      sx={{ width: 90, "& input": { color: appTheme.text, padding: "4px 6px", fontSize: "0.7rem" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "55" } }}
                    />
                  ) : (
                    <span style={{ color: sit.us_subcategory ? appTheme.text : "#475569" }}>{sit.us_subcategory || "—"}</span>
                  )}
                </TableCell>

                {/* Actions */}
                <TableCell align="center" sx={cellSx}>
                  {isEditing ? (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => saveEdit(sit.us_ID)}
                        sx={{
                          fontSize: "0.65rem",
                          minWidth: 0,
                          px: 1,
                          py: 0.3,
                        }}
                      >
                        {t("common.ok")}
                      </Button>
                      <Button
                        size="small"
                        onClick={() => cancelEdit(sit.us_ID)}
                        sx={{
                          fontSize: "0.65rem",
                          minWidth: 0,
                          px: 1,
                          py: 0.3,
                          color: appTheme.text,
                        }}
                      >
                        {t("common.cancel")}
                      </Button>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => startEdit(sit)}
                        sx={{
                          fontSize: "0.65rem",
                          minWidth: 0,
                          px: 1,
                          py: 0.3,
                          color: appTheme.text,
                          border: `1px solid ${appTheme.text}44`,
                        }}
                      >
                        {t("common.edit")}
                      </Button>
                      {sit.us_sit_ID === null && (
                        <Tooltip title={t("common.delete")}>
                          <IconButton
                            size="small"
                            onClick={() => deleteSit(sit)}
                            sx={{ color: "#f87171", p: 0.3 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}

          {/* Ligne d'ajout */}
          {newRow && (
            <TableRow>
              <TableCell sx={cellSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Tooltip title={categorizeSitS(newRow)}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: POLARITY_COLOR_S[categorizeSitS(newRow)], flexShrink: 0 }} />
                  </Tooltip>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <TextField
                      size="small"
                      placeholder={`FR – ${t("common.labelPlaceholder")}`}
                      value={newRow.us_label_fr}
                      onChange={(e) => setNewRow((prev) => ({ ...prev, us_label_fr: e.target.value }))}
                      sx={{ "& input": { color: appTheme.text, padding: "4px 8px", fontSize: "0.8rem" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "55" } }}
                    />
                    <TextField
                      size="small"
                      placeholder={`EN – ${t("common.labelPlaceholder")}`}
                      value={newRow.us_label_en}
                      onChange={(e) => setNewRow((prev) => ({ ...prev, us_label_en: e.target.value }))}
                      sx={{ "& input": { color: appTheme.text, padding: "4px 8px", fontSize: "0.8rem" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "55" } }}
                    />
                  </Box>
                </Box>
              </TableCell>
              {MANAS.map((m) => (
                <TableCell key={m.key} align="center" sx={cellSx}>
                  <TextField
                    size="small"
                    type="number"
                    value={newRow[m.key]}
                    onChange={(e) => setNewRow((prev) => ({ ...prev, [m.key]: parseInt(e.target.value) || 0 }))}
                    sx={inputSx}
                  />
                </TableCell>
              ))}
              <TableCell align="center" sx={cellSx}>—</TableCell>
              {/* Catégorie */}
              <TableCell align="center" sx={cellSx}>
                <Select
                  size="small"
                  value={newRow.us_category}
                  onChange={(e) => setNewRow((prev) => ({ ...prev, us_category: e.target.value }))}
                  sx={{ fontSize: "0.7rem", minWidth: 90, color: appTheme.text }}
                >
                  {CATEGORY_ORDER_S.map((c) => (
                    <MenuItem key={c} value={c} sx={{ fontSize: "0.75rem" }}>{t("categories." + CAT_I18N_KEY(c))}</MenuItem>
                  ))}
                </Select>
              </TableCell>
              {/* Sous-catégorie */}
              <TableCell align="center" sx={cellSx}>
                <TextField
                  size="small"
                  placeholder={t("common.optionalPlaceholder")}
                  value={newRow.us_subcategory}
                  onChange={(e) => setNewRow((prev) => ({ ...prev, us_subcategory: e.target.value }))}
                  sx={{ width: 90, "& input": { color: appTheme.text, padding: "4px 6px", fontSize: "0.7rem" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "55" } }}
                />
              </TableCell>
              <TableCell align="center" sx={cellSx}>
                <Box
                  sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    onClick={saveNewRow}
                    sx={{ fontSize: "0.65rem", minWidth: 0, px: 1, py: 0.3 }}
                  >
                    {t("common.ok")}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setNewRow(null)}
                    sx={{
                      fontSize: "0.65rem",
                      minWidth: 0,
                      px: 1,
                      py: 0.3,
                      color: appTheme.text,
                    }}
                  >
                    {t("common.cancel")}
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Button
        startIcon={<AddIcon />}
        onClick={addNewRow}
        disabled={!!newRow}
        sx={{
          mt: 1.5,
          color: appTheme.text,
          borderColor: appTheme.text + "55",
          fontSize: "0.8rem",
        }}
        variant="outlined"
        size="small"
      >
        {t("settings.situations.addBtn")}
      </Button>

      {Snack}
    </Box>
  );
}

// ─── Profil général (valeurs de référence des jauges) ───────────────────────
const PROFILE_GAUGES = [
  { key: "ug_defaultLogique",     labelKey: "gauges.logique",     max: 100, icon: <PsychologyIcon fontSize="small" /> },
  { key: "ug_defaultSocial",     labelKey: "gauges.social",     max: 100, icon: <GroupsIcon fontSize="small" /> },
  { key: "ug_defaultSensoriel",   labelKey: "gauges.sensoriel",   max: 100, icon: <HandScanIcon style={{ width: 20, height: 20 }} /> },
  { key: "ug_defaultPassion",     labelKey: "gauges.passion",     max: 100, icon: <FavoriteIcon fontSize="small" /> },
  { key: "ug_defaultMotivation", labelKey: "gauges.motivation", max: 100, icon: <LocalFireDepartmentIcon fontSize="small" /> },
  { key: "ug_defaultReserve",   labelKey: "gauges.reserve",   max: 300, icon: <BatterySaverIcon fontSize="small" /> },
  { key: "ug_defaultStamina",     labelKey: "gauges.stamina", max: 100, icon: <DirectionsRunIcon fontSize="small" /> },
];

function ProfileGaugesSection({ appTheme }) {
  const { t } = useTranslation();
  const [defaults, setDefaults] = useState(null);
  const { showSnack, Snack } = useSnack();

  useEffect(() => {
    fetch("/api/gauges/defaults", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setDefaults(data);
      })
      .catch(() => {});
  }, []);

  const handleChange = (key, rawVal) => {
    const parsed = parseInt(rawVal);
    if (isNaN(parsed)) return;
    const max = PROFILE_GAUGES.find((g) => g.key === key).max;
    setDefaults((prev) => ({
      ...prev,
      [key]: Math.min(max, Math.max(0, parsed)),
    }));
  };

  const handleBlur = async (key) => {
    const val = defaults[key];
    try {
      const res = await fetch("/api/gauges/defaults", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [key]: val }),
      });
      if (!res.ok) throw new Error();
    } catch {
      showSnack(t("common.errorSave"), "error");
    }
  };

  if (!defaults) return null;

  return (
    <Box data-tour="tour-settings-profile">
      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{ color: appTheme.text, fontWeight: "bold", mb: 0.5 }}
      >
        {t("settings.profile.title")}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: appTheme.text + "99", mb: 2, fontSize: "0.8rem" }}
      >
        {t("settings.profile.description")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {PROFILE_GAUGES.map(({ key, labelKey, max, icon }) => {
          const val = defaults[key] ?? 0;
          return (
            <Box
              key={key}
              sx={{ display: "flex", alignItems: "center", gap: 2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  width: 220,
                  flexShrink: 0,
                  color: appTheme.text,
                }}
              >
                {icon}
                <Typography sx={{ fontSize: "0.85rem" }}>{t(labelKey)}</Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: appTheme.text + "22",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${(val / max) * 100}%`,
                    height: "100%",
                    backgroundColor: "#60a5fa",
                    borderRadius: 3,
                    transition: "width 0.2s",
                  }}
                />
              </Box>
              <TextField
                type="number"
                size="small"
                value={val}
                onChange={(e) => handleChange(key, e.target.value)}
                onBlur={() => handleBlur(key)}
                inputProps={{ min: 0, max, step: 1 }}
                sx={{
                  width: 80,
                  flexShrink: 0,
                  "& input": {
                    color: appTheme.text,
                    textAlign: "center",
                    padding: "4px 6px",
                    fontSize: "0.85rem",
                    MozAppearance: "textfield",
                  },
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                    { display: "none" },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: appTheme.text + "44",
                  },
                }}
              />
              <Typography
                sx={{
                  color: appTheme.text + "55",
                  fontSize: "0.75rem",
                  width: 36,
                  flexShrink: 0,
                }}
              >
                /{max}
              </Typography>
            </Box>
          );
        })}
      </Box>
      {Snack}
    </Box>
  );
}

// ─── Tableau des débuts de journée ──────────────────────────────────────────
const MS_MANA_KEYS = ["ums_logique","ums_social","ums_sensoriel","ums_passion","ums_motivation","ums_reserve","ums_stamina"];
const categorizeMS = (row) => {
  const hasPos = MS_MANA_KEYS.some((k) => row[k] > 0);
  const hasNeg = MS_MANA_KEYS.some((k) => row[k] < 0);
  if (hasPos && !hasNeg) return "positive";
  if (hasNeg && !hasPos) return "negative";
  return "mixed";
};
const MANAS_MS = [
  { key: "ums_logique",     labelKey: "gauges.logique" },
  { key: "ums_social",     labelKey: "gauges.social" },
  { key: "ums_sensoriel",   labelKey: "gauges.sensoriel" },
  { key: "ums_passion",     labelKey: "gauges.passion" },
  { key: "ums_motivation", labelKey: "gauges.motivation" },
  { key: "ums_reserve",   labelKey: "gauges.reserve" },
  { key: "ums_stamina",     labelKey: "gauges.stamina" },
];

function MorningStartsTable({ appTheme }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage === "en" ? "en" : "fr";
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState({});
  const [newRow, setNewRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPolarity, setFilterPolarity] = useState("");
  const [filterOrigin, setFilterOrigin] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const { showSnack, Snack } = useSnack();

  useEffect(() => {
    fetch("/api/morning-starts", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setItems)
      .catch(() => {});
  }, []);

  const startEdit = (item) => {
    const row = { ums_label_fr: item.ums_label_fr || "", ums_label_en: item.ums_label_en || "", ums_active: item.ums_active };
    for (const m of MANAS_MS) row[m.key] = item[m.key];
    setEditing((prev) => ({ ...prev, [item.ums_ID]: row }));
  };

  const cancelEdit = (id) => {
    setEditing((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
  };

  const saveEdit = async (id) => {
    const body = { ...editing[id] };
    delete body._originals;
    try {
      const res = await fetch(`/api/morning-starts/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setItems((prev) => prev.map((i) => (i.ums_ID === id ? updated : i)));
      cancelEdit(id);
      showSnack(t("settings.morning.saved"), "success");
    } catch {
      showSnack(t("common.errorUpdate"), "error");
    }
  };

  const toggleActive = async (item) => {
    try {
      const res = await fetch(`/api/morning-starts/${item.ums_ID}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ums_active: !item.ums_active }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setItems((prev) =>
        prev.map((i) => (i.ums_ID === item.ums_ID ? updated : i)),
      );
    } catch {
      showSnack(t("common.errorToggle"), "error");
    }
  };

  const deleteItem = async (item) => {
    if (item.ums_ms_ID !== null) {
      showSnack(t("settings.morning.deactivateInstead"), "warning");
      return;
    }
    try {
      const res = await fetch(`/api/morning-starts/${item.ums_ID}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      setItems((prev) => prev.filter((i) => i.ums_ID !== item.ums_ID));
      showSnack(t("settings.morning.deleted"), "success");
    } catch {
      showSnack(t("common.errorDelete"), "error");
    }
  };

  const addNewRow = () => {
    const empty = { ums_label_fr: "", ums_label_en: "" };
    for (const m of MANAS_MS) empty[m.key] = 0;
    setNewRow(empty);
  };

  const saveNewRow = async () => {
    if (!newRow.ums_label_fr?.trim() && !newRow.ums_label_en?.trim()) {
      showSnack(t("common.labelRequired"), "warning");
      return;
    }
    try {
      const res = await fetch("/api/morning-starts", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newRow),
      });
      if (!res.ok) throw new Error();
      const created = await res.json();
      setItems((prev) => [...prev, created]);
      setNewRow(null);
      showSnack(t("settings.morning.added"), "success");
    } catch {
      showSnack(t("common.errorAdd"), "error");
    }
  };

  const cellSx = {
    color: appTheme.text,
    borderColor: appTheme.text + "33",
    padding: "6px 8px",
    fontSize: "0.75rem",
  };

  const inputSx = {
    width: 58,
    "& input": {
      color: appTheme.text,
      textAlign: "center",
      padding: "4px 6px",
      fontSize: "0.75rem",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "55" },
  };

  const filteredItems = items.filter((item) => {
    if (searchQuery.trim() !== "") {
      const q = searchQuery.trim().toLowerCase();
      const matchedManas = MANAS_MS.filter((m) => t(m.labelKey).toLowerCase().includes(q));
      const matchesSearch = resolveLabel(item, "ums_label", lang).toLowerCase().includes(q)
        || (matchedManas.length > 0 && matchedManas.some((m) => item[m.key] !== 0));
      if (!matchesSearch) return false;
    }
    if (filterPolarity && categorizeMS(item) !== filterPolarity) return false;
    if (filterOrigin === "default" && item.ums_ms_ID === null) return false;
    if (filterOrigin === "custom" && item.ums_ms_ID !== null) return false;
    if (filterStatus === "active" && !item.ums_active) return false;
    if (filterStatus === "inactive" && item.ums_active) return false;
    return true;
  });

  const toggleSxMS = {
    color: appTheme.text + "99",
    borderColor: appTheme.text + "33",
    fontSize: "0.65rem",
    py: 0.3,
    px: 0.8,
    "&.Mui-selected": { color: appTheme.text, backgroundColor: appTheme.text + "18" },
  };

  return (
    <Box data-tour="tour-settings-morning" sx={{ overflowX: "auto" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5, alignItems: "center" }}>
        <TextField
          size="small"
          placeholder={t("common.search")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: 300,
            "& input": { color: appTheme.text, fontSize: "0.8rem", padding: "6px 10px" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "44" },
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filterPolarity}
          onChange={(_, v) => setFilterPolarity(v ?? "")}
        >
          <ToggleButton value="" sx={toggleSxMS}>{t("common.filter.all")}</ToggleButton>
          <ToggleButton value="positive" sx={toggleSxMS}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: POLARITY_COLOR_S.positive, mr: 0.5 }} />
            {t("common.filter.positive")}
          </ToggleButton>
          <ToggleButton value="negative" sx={toggleSxMS}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: POLARITY_COLOR_S.negative, mr: 0.5 }} />
            {t("common.filter.negative")}
          </ToggleButton>
          <ToggleButton value="mixed" sx={toggleSxMS}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: POLARITY_COLOR_S.mixed, mr: 0.5 }} />
            {t("common.filter.mixed")}
          </ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filterOrigin}
          onChange={(_, v) => setFilterOrigin(v ?? "")}
        >
          <ToggleButton value="" sx={toggleSxMS}>{t("common.filter.all")}</ToggleButton>
          <ToggleButton value="default" sx={toggleSxMS}>{t("common.filter.default")}</ToggleButton>
          <ToggleButton value="custom" sx={toggleSxMS}>{t("common.filter.custom")}</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filterStatus}
          onChange={(_, v) => setFilterStatus(v ?? "")}
        >
          <ToggleButton value="" sx={toggleSxMS}>{t("common.filter.all")}</ToggleButton>
          <ToggleButton value="active" sx={toggleSxMS}>{t("common.filter.active")}</ToggleButton>
          <ToggleButton value="inactive" sx={toggleSxMS}>{t("common.filter.inactive")}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Table size="small" sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...cellSx, fontWeight: "bold" }}>
              {t("settings.morning.headerLabel")}
            </TableCell>
            {MANAS_MS.map((m) => (
              <TableCell
                key={m.key}
                align="center"
                sx={{ ...cellSx, fontWeight: "bold" }}
              >
                {t(m.labelKey)}
              </TableCell>
            ))}
            <TableCell align="center" sx={{ ...cellSx, fontWeight: "bold" }}>
              {t("common.active")}
            </TableCell>
            <TableCell align="center" sx={{ ...cellSx, fontWeight: "bold" }}>
              {t("common.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredItems.map((item) => {
            const isEditing = !!editing[item.ums_ID];
            const row = isEditing ? editing[item.ums_ID] : item;
            return (
              <TableRow
                key={item.ums_ID}
                sx={{ opacity: item.ums_active ? 1 : 0.45 }}
              >
                <TableCell sx={cellSx}>
                  {isEditing ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <TextField
                        size="small"
                        placeholder="FR"
                        value={row.ums_label_fr}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [item.ums_ID]: { ...prev[item.ums_ID], ums_label_fr: e.target.value },
                          }))
                        }
                        sx={{ "& input": { color: appTheme.text, padding: "4px 8px", fontSize: "0.8rem" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "55" } }}
                      />
                      <TextField
                        size="small"
                        placeholder="EN"
                        value={row.ums_label_en}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [item.ums_ID]: { ...prev[item.ums_ID], ums_label_en: e.target.value },
                          }))
                        }
                        sx={{ "& input": { color: appTheme.text, padding: "4px 8px", fontSize: "0.8rem" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "55" } }}
                      />
                    </Box>
                  ) : (
                    resolveLabel(item, "ums_label", lang)
                  )}
                </TableCell>

                {MANAS_MS.map((m) => (
                  <TableCell key={m.key} align="center" sx={cellSx}>
                    {isEditing ? (
                      <TextField
                        size="small"
                        type="number"
                        value={row[m.key]}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [item.ums_ID]: {
                              ...prev[item.ums_ID],
                              [m.key]: parseInt(e.target.value) || 0,
                            },
                          }))
                        }
                        sx={inputSx}
                      />
                    ) : (
                      <span
                        style={{
                          color:
                            item[m.key] > 0
                              ? "#4ade80"
                              : item[m.key] < 0
                                ? "#f87171"
                                : appTheme.text,
                        }}
                      >
                        {item[m.key] > 0 ? `+${item[m.key]}` : item[m.key]}
                      </span>
                    )}
                  </TableCell>
                ))}

                <TableCell align="center" sx={cellSx}>
                  <Switch
                    checked={isEditing ? row.ums_active : item.ums_active}
                    size="small"
                    onChange={() =>
                      isEditing
                        ? setEditing((prev) => ({
                            ...prev,
                            [item.ums_ID]: {
                              ...prev[item.ums_ID],
                              ums_active: !prev[item.ums_ID].ums_active,
                            },
                          }))
                        : toggleActive(item)
                    }
                    sx={{
                      "& .MuiSwitch-track": {
                        backgroundColor: appTheme.text + "44",
                      },
                    }}
                  />
                </TableCell>

                <TableCell align="center" sx={cellSx}>
                  {isEditing ? (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => saveEdit(item.ums_ID)}
                        sx={{
                          fontSize: "0.65rem",
                          minWidth: 0,
                          px: 1,
                          py: 0.3,
                        }}
                      >
                        {t("common.ok")}
                      </Button>
                      <Button
                        size="small"
                        onClick={() => cancelEdit(item.ums_ID)}
                        sx={{
                          fontSize: "0.65rem",
                          minWidth: 0,
                          px: 1,
                          py: 0.3,
                          color: appTheme.text,
                        }}
                      >
                        {t("common.cancel")}
                      </Button>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => startEdit(item)}
                        sx={{
                          fontSize: "0.65rem",
                          minWidth: 0,
                          px: 1,
                          py: 0.3,
                          color: appTheme.text,
                          border: `1px solid ${appTheme.text}44`,
                        }}
                      >
                        {t("common.edit")}
                      </Button>
                      {item.ums_ms_ID === null && (
                        <Tooltip title={t("common.delete")}>
                          <IconButton
                            size="small"
                            onClick={() => deleteItem(item)}
                            sx={{ color: "#f87171", p: 0.3 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}

          {newRow && (
            <TableRow>
              <TableCell sx={cellSx}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <TextField
                    size="small"
                    placeholder={`FR – ${t("common.labelPlaceholder")}`}
                    value={newRow.ums_label_fr}
                    onChange={(e) => setNewRow((prev) => ({ ...prev, ums_label_fr: e.target.value }))}
                    sx={{ "& input": { color: appTheme.text, padding: "4px 8px", fontSize: "0.8rem" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "55" } }}
                  />
                  <TextField
                    size="small"
                    placeholder={`EN – ${t("common.labelPlaceholder")}`}
                    value={newRow.ums_label_en}
                    onChange={(e) => setNewRow((prev) => ({ ...prev, ums_label_en: e.target.value }))}
                    sx={{ "& input": { color: appTheme.text, padding: "4px 8px", fontSize: "0.8rem" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "55" } }}
                  />
                </Box>
              </TableCell>
              {MANAS_MS.map((m) => (
                <TableCell key={m.key} align="center" sx={cellSx}>
                  <TextField
                    size="small"
                    type="number"
                    value={newRow[m.key]}
                    onChange={(e) =>
                      setNewRow((prev) => ({
                        ...prev,
                        [m.key]: parseInt(e.target.value) || 0,
                      }))
                    }
                    sx={inputSx}
                  />
                </TableCell>
              ))}
              <TableCell align="center" sx={cellSx}>
                —
              </TableCell>
              <TableCell align="center" sx={cellSx}>
                <Box
                  sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    onClick={saveNewRow}
                    sx={{ fontSize: "0.65rem", minWidth: 0, px: 1, py: 0.3 }}
                  >
                    {t("common.ok")}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setNewRow(null)}
                    sx={{
                      fontSize: "0.65rem",
                      minWidth: 0,
                      px: 1,
                      py: 0.3,
                      color: appTheme.text,
                    }}
                  >
                    {t("common.cancel")}
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Button
        startIcon={<AddIcon />}
        onClick={addNewRow}
        disabled={!!newRow}
        sx={{
          mt: 1.5,
          color: appTheme.text,
          borderColor: appTheme.text + "55",
          fontSize: "0.8rem",
        }}
        variant="outlined"
        size="small"
      >
        {t("settings.morning.addBtn")}
      </Button>

      {Snack}
    </Box>
  );
}

// ─── Onglet Sécurité ────────────────────────────────────────────────────────
function SecurityTab({ userId, appTheme }) {
  const { t } = useTranslation();
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPwd, setShowPwd] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const { showSnack, Snack } = useSnack();

  const handleChangePassword = async () => {
    if (newPwd !== confirmPwd) {
      showSnack(t("settings.security.mismatch"), "warning");
      return;
    }
    try {
      const res = await fetch(`/api/updatePwd/${userId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ old_password: oldPwd, new_password: newPwd }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        showSnack(
          payload?.message || t("settings.security.defaultError"),
          "error",
        );
        return;
      }
      setOldPwd("");
      setNewPwd("");
      setConfirmPwd("");
      showSnack(t("settings.security.success"), "success");
    } catch {
      showSnack(t("settings.security.error"), "error");
    }
  };

  const fieldSx = {
    mb: 2,
    "& label": { color: appTheme.text + "99" },
    "& input": { color: appTheme.text },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "44" },
    "& label.Mui-focused": { color: appTheme.text },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: appTheme.text,
    },
  };

  const eyeBtn = (key) => (
    <InputAdornment position="end">
      <IconButton
        onClick={() => setShowPwd((prev) => ({ ...prev, [key]: !prev[key] }))}
        edge="end"
        sx={{ color: appTheme.text + "99" }}
      >
        {showPwd[key] ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ color: appTheme.text }}>
        {t("settings.security.title")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0,
          p: 2,
          border: `1px solid ${appTheme.text}22`,
          borderRadius: 1,
          minWidth: 360,
          maxWidth: 480,
          mx: "auto",
        }}
      >
        <TextField
          label={t("settings.security.currentPassword")}
          type={showPwd.old ? "text" : "password"}
          value={oldPwd}
          onChange={(e) => setOldPwd(e.target.value)}
          sx={fieldSx}
          InputProps={{ endAdornment: eyeBtn("old") }}
        />
        <TextField
          label={t("settings.security.newPassword")}
          type={showPwd.new ? "text" : "password"}
          value={newPwd}
          onChange={(e) => setNewPwd(e.target.value)}
          sx={fieldSx}
          InputProps={{ endAdornment: eyeBtn("new") }}
        />
        <TextField
          label={t("settings.security.confirmPassword")}
          type={showPwd.confirm ? "text" : "password"}
          value={confirmPwd}
          onChange={(e) => setConfirmPwd(e.target.value)}
          onPaste={(e) => e.preventDefault()}
          sx={fieldSx}
          InputProps={{ endAdornment: eyeBtn("confirm") }}
        />

        <Button variant="contained" onClick={handleChangePassword} sx={{ mt: 1, alignSelf: "flex-start" }}>
          {t("settings.security.submit")}
        </Button>
      </Box>

      {Snack}
    </Box>
  );
}

// ─── Section upgrade proche → utilisateur ───────────────────────────────────
function UpgradeSection({ appTheme }) {
  const { t } = useTranslation();
  const { refreshUser } = useContext(AuthContext);
  const { showSnack, Snack } = useSnack();
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/proche/upgrade", {
        method: "PUT",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) {
        showSnack(data.message || t("settings.upgrade.error"), "error");
        return;
      }
      showSnack(t("settings.upgrade.success"), "success");
      await refreshUser();
    } catch {
      showSnack(t("common.networkError"), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {Snack}
      <Typography variant="subtitle1" gutterBottom sx={{ color: appTheme.text, fontWeight: "bold" }}>
        {t("settings.upgrade.title")}
      </Typography>
      <Box
        sx={{
          p: 2,
          border: `1px solid ${appTheme.text}22`,
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Typography sx={{ color: appTheme.text + "88", fontSize: "0.85rem", mb: 1.5 }}>
          {t("settings.upgrade.description1")}
        </Typography>
        <Typography sx={{ color: appTheme.text + "88", fontSize: "0.85rem", mb: 2 }}>
          {t("settings.upgrade.description2")}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={handleUpgrade}
          disabled={loading}
          sx={{
            color: "#4ade80",
            borderColor: "#4ade8055",
            "&:hover": { borderColor: "#4ade80" },
            "&.Mui-disabled": { color: appTheme.text + "44", borderColor: appTheme.text + "22" },
          }}
        >
          {loading ? t("settings.upgrade.loading") : t("settings.upgrade.button")}
        </Button>
      </Box>
    </Box>
  );
}

// ─── Onglet Mes proches ──────────────────────────────────────────────────────
function ProchesTab({ appTheme }) {
  const { t } = useTranslation();
  const { fetchAccessibleUsers } = useAuth();
  const { showSnack, Snack } = useSnack();

  const [inviteEmail, setInviteEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [proximates, setProximates] = useState([]); // proches qui ont accès à moi
  const [pendingInvitations, setPendingInvitations] = useState([]); // invitations en attente

  const loadInvited = () => {
    fetch("/api/proche/invited", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { proximates: [], invitations: [] }))
      .then((data) => {
        setProximates(data.proximates ?? []);
        setPendingInvitations((data.invitations ?? []).filter((i) => !i.inv_used && new Date(i.inv_expires_at) > new Date()));
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadInvited();
  }, []); // eslint-disable-line

  const handleInvite = async () => {
    if (!inviteEmail) return;
    setSending(true);
    try {
      const res = await fetch("/api/proche/invite", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: inviteEmail }),
      });
      const data = await res.json();
      if (!res.ok) {
        showSnack(data.message || t("settings.proches.sendError"), "error");
        return;
      }
      showSnack(data.direct ? t("settings.proches.directAccess") : t("settings.proches.inviteSent"), "success");
      setInviteEmail("");
      loadInvited();
    } catch {
      showSnack("Erreur réseau.", "error");
    } finally {
      setSending(false);
    }
  };

  const handleRevoke = async (targetUserId) => {
    try {
      const res = await fetch(`/api/proche/access/${targetUserId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        showSnack(t("settings.proches.revokeError"), "error");
        return;
      }
      showSnack(t("settings.proches.revokeSuccess"), "success");
      loadInvited();
      fetchAccessibleUsers(); // met à jour le menu déroulant de l'autre côté
    } catch {
      showSnack(t("common.networkError"), "error");
    }
  };

  const rowSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    p: 1,
    borderRadius: 1,
    border: `1px solid ${appTheme.text}22`,
    mb: 0.75,
  };

  return (
    <Box data-tour="tour-settings-proches">
      {Snack}

      {/* ── Inviter un proche ── */}
      <Typography variant="subtitle1" gutterBottom sx={{ color: appTheme.text, fontWeight: "bold" }}>
        {t("settings.proches.inviteTitle")}
      </Typography>
      <Typography variant="body2" sx={{ color: appTheme.text + "99", mb: 2, fontSize: "0.8rem" }}>
        {t("settings.proches.inviteDescription")}
      </Typography>
      <Box sx={{ display: "flex", gap: 1, mb: 3, alignItems: "flex-start" }}>
        <TextField
          label={t("settings.proches.emailLabel")}
          type="email"
          size="small"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && inviteEmail && !sending) handleInvite(); }}
          sx={{
            flex: 1,
            "& label": { color: appTheme.text + "99" },
            "& input": { color: appTheme.text },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: appTheme.text + "44" },
          }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={handleInvite}
          disabled={!inviteEmail || sending}
          sx={{
            color: "#93c5fd",
            borderColor: "#1e3a5f",
            whiteSpace: "nowrap",
            "&:hover": { borderColor: "#93c5fd" },
            "&.Mui-disabled": { color: appTheme.text + "44", borderColor: appTheme.text + "22" },
          }}
        >
          {sending ? t("settings.proches.sending") : t("settings.proches.sendBtn")}
        </Button>
      </Box>

      <Divider sx={{ mb: 2.5, borderColor: appTheme.text + "33" }} />

      {/* ── Proches qui ont accès ── */}
      <Typography variant="subtitle1" gutterBottom sx={{ color: appTheme.text, fontWeight: "bold" }}>
        {t("settings.proches.accessTitle")}
      </Typography>
      {proximates.length === 0 ? (
        <Typography sx={{ color: appTheme.text + "66", fontSize: "0.85rem", mb: 2 }}>
          {t("settings.proches.noAccess")}
        </Typography>
      ) : (
        <Box sx={{ mb: 2 }}>
          {proximates.map((p) => (
            <Box key={p.users_ID} sx={rowSx}>
              <Box>
                <Typography sx={{ color: appTheme.text, fontSize: "0.9rem", fontWeight: "bold" }}>
                  {p.users_pseudo}
                </Typography>
                <Typography sx={{ color: appTheme.text + "77", fontSize: "0.75rem" }}>
                  {p.users_email}
                </Typography>
              </Box>
              <Tooltip title={t("settings.proches.revokeTooltip")}>
                <IconButton
                  size="small"
                  onClick={() => handleRevoke(p.users_ID)}
                  sx={{ color: "#f87171", "&:hover": { color: "#ef4444" } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      {/* ── Invitations en attente ── */}
      {pendingInvitations.length > 0 && (
        <>
          <Divider sx={{ mb: 2, borderColor: appTheme.text + "22" }} />
          <Typography variant="subtitle2" gutterBottom sx={{ color: appTheme.text + "99", fontWeight: "bold" }}>
            {t("settings.proches.pendingTitle")}
          </Typography>
          {pendingInvitations.map((inv) => (
            <Box key={inv.inv_ID} sx={{ ...rowSx, border: `1px solid ${appTheme.text}15` }}>
              <Typography sx={{ color: appTheme.text + "88", fontSize: "0.85rem" }}>
                {inv.inv_email}
              </Typography>
              <Typography sx={{ color: appTheme.text + "55", fontSize: "0.75rem" }}>
                {t("settings.proches.expiresOn", { date: new Date(inv.inv_expires_at).toLocaleDateString() })}
              </Typography>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
}

// ─── Page principale ────────────────────────────────────────────────────────
export default function Settings() {
  const { t } = useTranslation();
  const { state: user, loading } = useContext(AuthContext);
  const { appTheme } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("identity");

  useEffect(() => {
    if (!loading && !user) navigate("/connexion");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (location.state?.tourTab) {
      setActiveTab(location.state.tourTab);
    }
  }, [location.state]);

  useEffect(() => {
    const handler = (e) => setActiveTab(e.detail);
    window.addEventListener("tour-set-tab", handler);
    return () => window.removeEventListener("tour-set-tab", handler);
  }, []);

  if (loading || !user) return null;

  const isProche = user.users_status === "p";

  const tabs = [
    { key: "identity", label: t("settings.tabs.identity") },
    ...(!isProche ? [
      { key: "situations", label: t("settings.tabs.situations") },
      { key: "morning", label: t("settings.tabs.morning") },
      { key: "proches", label: t("settings.tabs.proches") },
    ] : []),
    { key: "security", label: t("settings.tabs.security") },
  ];

  const tabBtnSx = (key) => ({
    flex: 1,
    borderRadius: 0,
    borderBottom:
      activeTab === key
        ? `2px solid ${appTheme.text}`
        : "2px solid transparent",
    color: activeTab === key ? appTheme.text : appTheme.text + "66",
    fontWeight: activeTab === key ? "bold" : "normal",
    py: 1.2,
    textTransform: "none",
    fontSize: "0.9rem",
  });

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: appTheme.bg,
        color: appTheme.text,
        transition: "background-color 0.3s ease, color 0.3s ease",
        fontFamily: "inherit",
      }}
    >
      <Header />

      <Box sx={{ mt: 4, px: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ color: appTheme.text }}>
          {t("settings.title")}
        </Typography>
        <Divider sx={{ mb: 0, borderColor: appTheme.text + "33" }} />

        {/* Onglets */}
        <Box
          sx={{
            display: "flex",
            mb: 3,
            borderBottom: `1px solid ${appTheme.text}22`,
          }}
        >
          {tabs.map(({ key, label }) => (
            <Button
              key={key}
              onClick={() => setActiveTab(key)}
              sx={tabBtnSx(key)}
            >
              {label}
            </Button>
          ))}
        </Box>

        {/* ── Onglet Identité & Profil ── */}
        {activeTab === "identity" && (
          <Box>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ color: appTheme.text, fontWeight: "bold" }}
            >
              {t("settings.identity.sectionTitle")}
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                gap: 0.5,
                mb: 3,
                p: 1.5,
                border: `1px solid ${appTheme.text}22`,
                borderRadius: 1,
              }}
            >
              <Typography sx={{ color: appTheme.text, fontSize: "0.9rem" }}>
                {t("settings.identity.id")}<strong>#{user.users_ID}</strong>
              </Typography>
              <Typography sx={{ color: appTheme.text, fontSize: "0.9rem" }}>
                {t("settings.identity.pseudo")}<strong>{user.users_pseudo}</strong>
              </Typography>
              <Typography sx={{ color: appTheme.text, fontSize: "0.9rem" }}>
                {t("settings.identity.email")}<strong>{user.users_email}</strong>
              </Typography>
            </Box>

            <Divider sx={{ mb: 2.5, borderColor: appTheme.text + "33" }} />

            {isProche ? (
              <UpgradeSection appTheme={appTheme} />
            ) : (
              <ProfileGaugesSection appTheme={appTheme} />
            )}
          </Box>
        )}

        {/* ── Onglet Situations ── */}
        {activeTab === "situations" && (
          <Box>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ color: appTheme.text, fontWeight: "bold", mb: 1.5 }}
            >
              {t("settings.situations.title")}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: appTheme.text + "99", mb: 2, fontSize: "0.8rem" }}
            >
              {t("settings.situations.description")}
            </Typography>
            <SituationsTable appTheme={appTheme} />
          </Box>
        )}

        {/* ── Onglet Débuts de journée ── */}
        {activeTab === "morning" && (
          <Box>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ color: appTheme.text, fontWeight: "bold", mb: 1.5 }}
            >
              {t("settings.morning.title")}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: appTheme.text + "99", mb: 2, fontSize: "0.8rem" }}
            >
              {t("settings.morning.description")}
            </Typography>
            <MorningStartsTable appTheme={appTheme} />
          </Box>
        )}

        {/* ── Onglet Mes proches ── */}
        {activeTab === "proches" && (
          <ProchesTab appTheme={appTheme} />
        )}

        {/* ── Onglet Sécurité ── */}
        {activeTab === "security" && (
          <SecurityTab userId={user.users_ID} appTheme={appTheme} />
        )}
      </Box>
    </div>
  );
}
